/**
 * SynopsisWindow 分頁的共用純工具（ADR-0001 的 B 層共用層）。
 *
 * 這些 helper 原本在 splitForPlainText / splitForTopic108_2 / splitForSimple108_2
 * 三檔中各有一份逐字相同的拷貝，slice 2 將其收斂到此單一來源，消除 drift。
 * 皆為純函數、無狀態，與 per-style 切頁規則無關。
 *
 * 使用範圍：
 * - 三種樣式皆用：getAttributesString / getAttributesStringExcluding /
 *   isSelfClosingTag / wrapWithOuterWrappers
 * - 僅 PlainText 與 Topic 使用：extractOriginalElementInfo /
 *   wrapWithOriginalElement / buildWrapperTag
 */

// 移除 MathML annotation，避免 MTEF 編碼在舊版 Chromium（Electron）顯示為亂碼
export const stripMathAnnotations = (html) => {
  if (!html || typeof html !== 'string') return html;
  return html
    .replace(/<annotation-xml[^>]*>[\s\S]*?<\/annotation-xml>/gi, '')
    .replace(/<annotation[^>]*>[\s\S]*?<\/annotation>/gi, '');
};

// DOM 版：移除測量容器中的 <annotation> / <annotation-xml>，
// 確保 MTEF 編碼不撐高離屏測量容器、不干擾行數判斷
export const hideMathAnnotations = (container) => {
  container.querySelectorAll('annotation, annotation-xml').forEach(el => el.remove());
};

/**
 * 獲取元素的屬性字串
 */
export const getAttributesString = (element) => {
  let attrs = '';
  if (element.attributes && element.attributes.length > 0) {
    Array.from(element.attributes).forEach(attr => {
      attrs += ` ${attr.name}="${attr.value}"`;
    });
  }
  return attrs;
};

/**
 * 獲取元素的屬性字串（排除指定的屬性）
 */
export const getAttributesStringExcluding = (element, excludeAttrs = []) => {
  let attrs = '';
  if (element.attributes && element.attributes.length > 0) {
    Array.from(element.attributes).forEach(attr => {
      if (!excludeAttrs.includes(attr.name)) {
        attrs += ` ${attr.name}="${attr.value}"`;
      }
    });
  }
  return attrs;
};

/**
 * 判斷是否為自閉合標籤
 */
export const isSelfClosingTag = (tagName) => {
  const selfClosingTags = ['BR', 'IMG', 'HR', 'INPUT', 'META', 'LINK', 'AREA', 'BASE', 'COL', 'EMBED', 'SOURCE', 'TRACK', 'WBR'];
  return selfClosingTags.includes(tagName.toUpperCase());
};

/**
 * 用外層包裝元素包裹 HTML 內容
 * @param {string} htmlContent - 要包裹的 HTML 內容
 * @param {Array} outerWrappers - 外層包裝元素信息數組
 * @param {Document} doc - 文檔對象
 * @returns {string} 包裹後的 HTML 字串
 */
export const wrapWithOuterWrappers = (htmlContent, outerWrappers, doc) => {
  if (!outerWrappers || outerWrappers.length === 0) {
    return htmlContent;
  }

  // 從最內層到最外層依次包裹
  let wrappedContent = htmlContent;

  for (const wrapper of outerWrappers) {
    let tag = `<${wrapper.tagName}`;
    if (wrapper.id) {
      tag += ` id="${wrapper.id}"`;
    }
    if (wrapper.className) {
      tag += ` class="${wrapper.className}"`;
    }
    if (wrapper.attributes) {
      tag += wrapper.attributes;
    }
    tag += '>';

    wrappedContent = tag + wrappedContent + `</${wrapper.tagName}>`;
  }

  return wrappedContent;
};

/**
 * 從元素中提取原始元素信息（className, id, tagName）
 * 會向上查找，直到找到有 className 或 id 的元素
 */
export const extractOriginalElementInfo = (element) => {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  // 如果當前元素有 className 或 id，直接返回
  if (element.className || element.id) {
    return {
      tagName: element.tagName.toLowerCase(),
      className: element.className || '',
      id: element.id || '',
      attributes: getAttributesStringExcluding(element, ['class', 'id'])
    };
  }

  // 否則向上查找父元素
  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    if (parent.className || parent.id) {
      return {
        tagName: parent.tagName.toLowerCase(),
        className: parent.className || '',
        id: parent.id || '',
        attributes: getAttributesStringExcluding(parent, ['class', 'id'])
      };
    }
    parent = parent.parentElement;
  }

  return null;
};

/**
 * 用原始元素的結構包裹內容
 */
export const wrapWithOriginalElement = (content, originalElementInfo, doc) => {
  if (!originalElementInfo || (!originalElementInfo.className && !originalElementInfo.id)) {
    // 沒有原始元素信息，直接創建一個 div 包裹內容
    if (typeof content === 'string') {
      const wrapper = doc.createElement('div');
      wrapper.innerHTML = content;
      return wrapper;
    }
    return content;
  }

  // 創建原始元素的結構
  const wrapper = doc.createElement(originalElementInfo.tagName);
  if (originalElementInfo.className) {
    wrapper.className = originalElementInfo.className;
  }
  if (originalElementInfo.id) {
    wrapper.id = originalElementInfo.id;
  }

  // 設置其他屬性
  if (originalElementInfo.attributes) {
    const parser = new DOMParser();
    const tempDoc = parser.parseFromString(`<div ${originalElementInfo.attributes}></div>`, 'text/html');
    const tempEl = tempDoc.body.firstElementChild;
    if (tempEl) {
      Array.from(tempEl.attributes).forEach(attr => {
        if (attr.name !== 'class' && attr.name !== 'id') {
          wrapper.setAttribute(attr.name, attr.value);
        }
      });
    }
  }

  // 設置內容
  if (typeof content === 'string') {
    wrapper.innerHTML = content;
  } else {
    wrapper.appendChild(content);
  }

  return wrapper;
};

/**
 * 構建包裹標籤（用於測量）
 */
export const buildWrapperTag = (originalElementInfo) => {
  if (!originalElementInfo || (!originalElementInfo.className && !originalElementInfo.id)) {
    return { startTag: '<div>', endTag: '</div>' };
  }

  let tag = `<${originalElementInfo.tagName}`;
  if (originalElementInfo.id) {
    tag += ` id="${originalElementInfo.id}"`;
  }
  if (originalElementInfo.className) {
    tag += ` class="${originalElementInfo.className}"`;
  }
  if (originalElementInfo.attributes) {
    tag += originalElementInfo.attributes;
  }
  tag += '>';

  return {
    startTag: tag,
    endTag: `</${originalElementInfo.tagName}>`
  };
};
